import { setUser as setSentryUser } from '@sentry/react';
import AuthContext from './auth-context';
import { useCallback, useEffect, useState } from 'react';
import { IUser } from './auth-context';
import config, { convergeLogger } from '@converge/config';
import { datadogRum } from '@datadog/browser-rum';
import { jwtDecode } from 'jwt-decode';
import moment from 'moment';

export const isLocalEnv = config.convergeEnv === 'local';
const clearBrowserCache = async () => {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((cache) => caches.delete(cache)));
  }
};

export const logoutUserSSO = async () => {
  await clearBrowserCache();
  if (config.convergeEnv !== 'local' && hasSSOCookies()) {
    window.location.assign('/logout');
  }
};

export type JWTLoginData = {
  userUuid?: string;
};

type AuthProviderProps = {
  children: JSX.Element;
  onUserChange?: (user: IUser | null) => void;
};

interface CustomJwtToken {
  exp: number;
  auth_time: number;
  username: string;
  email: string;
  client_id: string;
  scope: string;
  'custom:group_name'?: string;
  'custom:organization'?: string;
  'custom:tenant'?: string;
  given_name?: string;
  family_name?: string;
  email_verified?: boolean;
}

/**
 * Parses the custom:group_name string from the ID token
 * and returns an array of groups filtered by environment
 */
export function parseGroupsFromIdToken(idToken: string, env: string): string[] {
  try {
    const idTokenData = jwtDecode<CustomJwtToken>(idToken);
    const groupNameStr = idTokenData['custom:group_name'] || '';

    // Map environment to the appropriate group prefix
    let groupPrefix: string;
    switch (env) {
      case 'production':
        groupPrefix = 'prod';
        break;
      case 'staging':
        groupPrefix = 'staging';
        break;
      case 'qa':
        groupPrefix = 'qa';
        break;
      case 'development':
      case 'local':
      default:
        groupPrefix = 'dev';
        break;
    }

    // Format is "[dev_group1, dev_group2]" - split by comma and clean up
    // Handle empty string case
    if (!groupNameStr) {
      return [];
    }

    const groups = groupNameStr
      .replace(/^\[|\]$/g, '') // Remove outer brackets if present
      .split(',')
      .map((group) => group.trim())
      .filter((group) => group !== ''); // Filter out empty strings

    // Get environment-specific groups (with prefix)
    const envSpecificGroups = groups
      .filter((group) => group.startsWith(`${groupPrefix}_`))
      .map((group) => {
        // Extract the group name without the environment prefix
        const parts = group.split('_');
        return parts.length > 1 ? parts.slice(1).join('_') : group;
      });

    // Get global groups (without any environment prefix)
    const globalGroups = groups.filter(
      (group) =>
        !group.startsWith('dev_') &&
        !group.startsWith('qa_') &&
        !group.startsWith('staging_') &&
        !group.startsWith('prod_')
    );

    // Log groups for debugging
    convergeLogger.log(`Environment prefix: ${groupPrefix}`);
    convergeLogger.log(
      `Environment-specific groups: ${envSpecificGroups.join(', ')}`
    );
    convergeLogger.log(
      `Global groups (non-prefixed): ${globalGroups.join(', ')}`
    );

    // Combine both sets of groups
    return [...envSpecificGroups, ...globalGroups];
  } catch (error) {
    convergeLogger.error('Error parsing groups from ID token:', error);
    return [];
  }
}

export function isCurrentUserValid(
  accessToken: string,
  idToken: string
): boolean {
  try {
    // Extract tenant from ID token
    const idTokenData = jwtDecode<CustomJwtToken>(idToken);
    const tenant = idTokenData['custom:tenant'] || '';

    // Parse groups from ID token
    const currentEnv = config.convergeEnv;
    const groups = parseGroupsFromIdToken(idToken, currentEnv);

    convergeLogger.log(`Parsed groups for environment ${currentEnv}:`, groups);
    convergeLogger.log(`User tenant: ${tenant}`);

    // Check for required groups
    const hasAdminGroup = groups.some(
      (group) => group === 'converge-admin' || group === 'admin'
    );

    // Check for tenant-specific uiuser group
    const hasTenantUiUserGroup = tenant
      ? groups.some((group) => group === `${tenant}-uiuser`)
      : false;

    // User is valid if they belong to any of the allowed groups
    return hasAdminGroup || hasTenantUiUserGroup;
  } catch (error) {
    // Handle any JWT decoding errors gracefully
    convergeLogger.error('Error validating user token:', error);
    return false;
  }
}

export const hasSSOCookies = () => {
  return Object.keys(getSSOCookies()).length > 0;
};

export const getSSOCookies = () => {
  const cookies = document.cookie.split('; ');
  const cookieData: { [key: string]: string } = {};

  cookies.forEach((cookie) => {
    const [key, value] = cookie.split('=');
    if (key.startsWith('CognitoIdentityServiceProvider')) {
      const tokenTypeMatch = key.match(/accessToken|idToken|refreshToken/);
      if (tokenTypeMatch) {
        cookieData[tokenTypeMatch[0]] = decodeURIComponent(value);
      }
    }
  });
  return cookieData;
};

const refreshIfNoSSOCookies = () => {
  const hasCookies = hasSSOCookies();
  if (!hasCookies && !isLocalEnv) {
    window.location.reload();
  }
};

type cookieDataType = {
  [key: string]: string;
};

export const getExpiresEpoch = (cookieData: cookieDataType) => {
  const accessToken: string = cookieData['accessToken'] || '';
  const jwtData = jwtDecode<CustomJwtToken>(accessToken);
  return jwtData.exp;
};

export const getUserEmailFromToken = (idToken: string) => {
  const jwtIdData = jwtDecode<CustomJwtToken>(idToken);
  return {
    username: jwtIdData.email,
    firstName: jwtIdData.given_name || '',
    lastName: jwtIdData.family_name || '',
  };
};
export function GetCurrentUserFromCookies(): IUser | null {
  const userObj: IUser = {
    user: {
      uuid: '',
      username: '',
    },
    idToken: '',
    accessToken: '',
    refreshToken: '',
    expiresInSec: 0,
    expiresEpoch: 0,
    tokenType: 'Bearer',
  };

  refreshIfNoSSOCookies();
  const cookieData = getSSOCookies();
  userObj.accessToken = cookieData['accessToken'] || '';
  userObj.idToken = cookieData['idToken'] || '';
  userObj.refreshToken = cookieData['refreshToken'] || '';

  // Ensure both tokens are present
  if (userObj.accessToken && userObj.idToken) {
    try {
      const jwtData = jwtDecode<CustomJwtToken>(userObj.accessToken);
      const expiresEpoch = getExpiresEpoch(cookieData);
      userObj.expiresInSec = expiresEpoch - jwtData.auth_time;
      userObj.expiresEpoch = expiresEpoch;
      const { username, firstName, lastName } = getUserEmailFromToken(
        userObj.idToken
      );
      userObj.user.username = username;
      userObj.user.firstName = firstName;
      userObj.user.lastName = lastName;

      return userObj;
    } catch (error) {
      convergeLogger.error('Error decoding JWT:', error);
    }
  }

  return null;
}

const jwtLogin = async (token: string): Promise<JWTLoginData> => {
  try {
    const apiUrl = config.apiUrl;
    const response = await fetch(`${apiUrl}/jwt-login`, {
      method: 'POST',
      headers: {
        Authorization: `bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return (await response.json()) as JWTLoginData;
  } catch (error) {
    convergeLogger.error('Error during JWT login:', error);
    throw error;
  }
};

const getUserLocalStorage = () => {
  const userString = localStorage.getItem('currentUser');

  if (userString) {
    try {
      const user = JSON.parse(userString);
      if (user.accessToken && user.idToken) {
        const isValid = isCurrentUserValid(user.accessToken, user.idToken);
        if (!isValid) {
          localStorage.removeItem('currentUser');
          convergeLogger.error('User is invalid');
          return null;
        }
        return user;
      }
      // Missing required tokens
      localStorage.removeItem('currentUser');
      convergeLogger.error('User missing required tokens');
      return null;
    } catch {
      return null;
    }
  }
  return undefined;
};

const getUserCookies = () => {
  const user = GetCurrentUserFromCookies();
  if (user && user.accessToken && user.idToken) {
    const isValid = isCurrentUserValid(user.accessToken, user.idToken);
    if (!isValid) {
      convergeLogger.error('User is invalid');
      return { ...user, accessToken: '' };
    }
    return user;
  }
  return null;
};

export function GetCurrentUser(): IUser | null {
  const hasCookies = hasSSOCookies();
  if (!hasCookies || isLocalEnv) {
    return getUserLocalStorage();
  } else {
    return getUserCookies();
  }
}

export function GetAuthToken() {
  return GetCurrentUser()?.accessToken;
}

export function ResetUser(): void {
  convergeLogger.log(`Resetting user...`);
  sessionStorage.removeItem('jwtLoginDone');
  localStorage.removeItem('currentUserId');
  localStorage.removeItem('currentUser');
  localStorage.removeItem('refreshTokenExpiryDate');
}

export default function AuthProvider({
  children,
  onUserChange,
}: AuthProviderProps) {
  const [user, setUser] = useState<IUser | null>(GetCurrentUser());
  const [isUserIdle, setIsUserIdle] = useState<boolean>(false);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'currentUser') {
        convergeLogger.info(`User changed in another tab, updating...`);
        setUser(GetCurrentUser());
      }
    };

    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    if (!user?.user?.username) {
      if (config.enableSentry) {
        convergeLogger.log(`Clearing Sentry user...`);
        setSentryUser(null);
      }

      if (config.enableDataDog) {
        convergeLogger.log(`Clearing DataDog user...`);
        datadogRum.clearUser();
      }
      return;
    }

    if (config.enableSentry) {
      convergeLogger.log(`Setting Sentry user...`);
      setSentryUser({
        email: user.user.username,
      });
    }

    if (config.enableDataDog) {
      convergeLogger.log(
        `Setting DataDog user...`,
        datadogRum.getInternalContext()
      );
      datadogRum.setUser({
        id: user.user.uuid ?? 'n/a',
        email: user.user.username,
      });
    }

    if (onUserChange) {
      onUserChange(user);
    }
  }, [onUserChange, user]);

  const setSignedInUser = useCallback((user: IUser | null) => {
    return new Promise<void>((resolve, reject) => {
      try {
        const refreshTokenExpiryDate = moment(new Date())
          .utc()
          .add(30, 'days')
          .format('YYYY-MM-DDTHH:mm:ss');
        setUser(user);
        if (!user) {
          localStorage.removeItem('currentUserId');
          localStorage.removeItem('currentUser');
          localStorage.removeItem('refreshTokenExpiryDate');
        } else {
          if (user.user.uuid)
            localStorage.setItem('currentUserId', user.user.uuid);
          if (!hasSSOCookies())
            localStorage.setItem('currentUser', JSON.stringify(user));
          localStorage.setItem(
            'refreshTokenExpiryDate',
            JSON.stringify(refreshTokenExpiryDate)
          );
        }

        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }, []);

  useEffect(() => {
    const doJwtLogin = async () => {
      if (!user?.accessToken) return;

      const hasLoggedIn = sessionStorage.getItem('jwtLoginDone');
      if (hasLoggedIn || !hasSSOCookies()) return;

      try {
        const data = await jwtLogin(user.accessToken);
        if (data.userUuid) {
          setSignedInUser({
            ...user,
            user: {
              ...user.user,
              uuid: data.userUuid,
            },
          });
        }
        sessionStorage.setItem('jwtLoginDone', 'true');
      } catch (err) {
        convergeLogger.error('JWT login failed', err);
      }
    };

    doJwtLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.accessToken, setSignedInUser]);

  const resetUser = useCallback(() => {
    ResetUser();
    logoutUserSSO();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, setSignedInUser, resetUser, isUserIdle, setIsUserIdle }}
    >
      {children}
    </AuthContext.Provider>
  );
}
